import type { AdError, AdInfo } from '@softgames/ad-flow';
import type { MetaPropertiesGetter, OfferInteractionLocation, TrackingModule } from './types';
import type { AppState } from '../state/AppState';
import type { ResourceMap } from '../resources/types';
import type {
  MetaProperties,
  PaymentProviderValueType,
  ResourceActionTypeValueType,
  SeasonPassStatusContextValueType,
} from './trackingModules/avo/Avo';
import type { ReviveReason } from '../../game/GameConstants';
import type { IAPPlacementData, IAPProduct } from '../iap/types';
import type { Purchase } from '../platform/Platform';

export class AnalyticsTracker implements TrackingModule {
  private readonly modules: Partial<TrackingModule>[] = [];

  public addModule(module: Partial<TrackingModule>): void {
    this.modules.push(module);
  }

  public trackGameInitialized(
    data: AppState,
    props: {
      userId: string;
      entryPointName: string | null;
      entryPointData: unknown;
    },
  ): void {
    this.modules.forEach((module) => {
      module?.trackGameInitialized?.(data, props);
    });
  }

  public trackLevelStarted(properties: { levelNumber: number; lives: number; tileRvAmount: number }): void {
    this.modules.forEach((module) => {
      module?.trackLevelStarted?.(properties);
    });
  }

  public trackLevelWon(properties: {
    levelNumber: number;
    levelId: string;
    levelAttemptId: string;
    lives: number;
    doubleRewardShown: boolean;
    inventory: ResourceMap;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackLevelWon?.(properties);
    });
  }

  public trackLevelLost(properties: { levelNumber: number; lives: number; metaProperties?: MetaProperties[] }): void {
    this.modules.forEach((module) => {
      module?.trackLevelLost?.(properties);
    });
  }

  public trackReviveShown(properties: { levelNumber: number }): void {
    this.modules.forEach((module) => {
      module?.trackReviveShown?.(properties);
    });
  }

  public trackReviveUsed(properties: { levelNumber: number; reason: ReviveReason; resourceAmount: number }): void {
    this.modules.forEach((module) => {
      module?.trackReviveUsed?.(properties);
    });
  }

  public trackDioramaCompleted(dioramaIndex: number): void {
    this.modules.forEach((module) => {
      module?.trackDioramaCompleted?.(dioramaIndex);
    });
  }

  public trackAdFinished(properties: {
    adInfo: AdInfo;
    adError: AdError | null;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackAdFinished?.(properties);
    });
  }

  public splitTest(testSlug: string, distribution: number, metaProperties?: MetaProperties[]): void {
    this.modules.forEach((module) => {
      module?.splitTest?.(testSlug, distribution, metaProperties);
    });
  }

  public trackResourceAction(properties: {
    levelId: string;
    levelNumber: number;
    levelAttemptId: string;
    actionTrigger: string;
    actionType: ResourceActionTypeValueType;
    resourceUpdate: ResourceMap;
    inventory: ResourceMap;
    gameEventId?: string;
    transactionId?: string;
  }): void {
    this.modules.forEach((module) => {
      module?.trackResourceAction?.(properties);
    });
  }

  public trackPurchase(properties: {
    data: AppState;
    product: IAPProduct;
    purchase: Purchase;
    placementData: IAPPlacementData;
    offerImpressionId: string | null;
    paymentProvider: PaymentProviderValueType;
  }): void {
    this.modules.forEach((module) => {
      module?.trackPurchase?.(properties);
    });
  }

  public trackRvTileUnlock(properties: { levelAttemptId: string; trigger: 'ad' | 'coin' }): void {
    this.modules.forEach((module) => {
      module?.trackRvTileUnlock?.(properties);
    });
  }

  public addMetaPropertiesGetter(getter: MetaPropertiesGetter): void {
    this.modules.forEach((module) => {
      module?.addMetaPropertiesGetter?.(getter);
    });
  }

  public trackEventStarted(type: string, eventId: string): void {
    this.modules.forEach((module) => {
      module?.trackEventStarted?.(type, eventId);
    });
  }

  public trackEventFinished(properties: {
    type: string;
    eventId: string;
    progress: number;
    rank?: number;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackEventFinished?.(properties);
    });
  }

  public trackOfferClick(properties: {
    product: { id: string; variantId: string; activationId: string; platformId: string };
    location: OfferInteractionLocation;
    impressionId: string;
  }): void {
    this.modules.forEach((module) => {
      module?.trackOfferClick?.(properties);
    });
  }

  public trackOfferShown(properties: {
    product: { id: string; variantId: string; activationId: string; platformId: string };
    location: OfferInteractionLocation;
    impressionId: string;
  }): void {
    this.modules.forEach((module) => {
      module?.trackOfferShown?.(properties);
    });
  }

  public trackUIInteraction(properties: {
    elementId: string;
    contextName: string;
    action: 'View' | 'Click';
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackUIInteraction?.(properties);
    });
  }

  public trackSeasonPass(properties: {
    points: number;
    pointsIncrease: number;
    seasonPassId: string;
    eventContext: SeasonPassStatusContextValueType;
    productId?: string;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackSeasonPass?.(properties);
    });
  }

  public trackCollectionEventEnd(properties: {
    gameEventId: string;
    gameEventTier: number;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackCollectionEventEnd?.(properties);
    });
  }

  public trackCollectionEventTokenUpdate(properties: {
    gameEventId: string;
    tokenAmount: number;
    levelAttemptId: string;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackCollectionEventTokenUpdate?.(properties);
    });
  }

  public trackCheatsEnabled(): void {
    this.modules.forEach((module) => {
      module?.trackCheatsEnabled?.();
    });
  }

  public trackPlayerAttribution(properties: {
    attributionPayload: string;
    attributionNetwork?: string;
    attributionCampaign?: string;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackPlayerAttribution?.(properties);
    });
  }

  public trackNativeAppLaunched(properties: {
    appBuildNumber: number;
    appVersion: string;
    memoryTotal?: number;
    memoryAvailable?: number;
    assetQuality?: 'low' | 'high';
    deviceIdentifier: string;
    metaProperties?: MetaProperties[];
  }): void {
    this.modules.forEach((module) => {
      module?.trackNativeAppLaunched?.(properties);
    });
  }
}
